import React, { useState } from 'react';

import { createBasicBorrowingBaseReportWithCalcs } from '@finley-technologies/calc-agent';
import { finleyAssert } from '@finley-technologies/common';
import { Util, DbTable, Receivable } from '@finley-technologies/finley-types';
import {
  TapeBuilderUI,
  OperationNodes,
} from '@finley-technologies/tape-builder';

import { getCreditFacilityBySlug, useGraph } from '../GraphContext';
import { ProgressWithDownloadButton } from './ProgressWithDownloadButton';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

// CreditAgreement.Config.ResolvedLoanProductMechanics
type BasicBorrowingBaseEvaluatorProps = {
  borrowingBaseMechanics: CreditAgreement.Config.ResolvedCalcAgentMechanics;
  borrowingBaseReceivables: Receivable.CreditFacility.BorrowingBaseReceivable[];
  concentrationLimitCalculations: CreditAgreement.Config.ConcentrationLimit.Calculation.ConcentrationLimitCalculation[];
  tapeBuilderNode: OperationNodes.CalcAgent.UINode;
  reportingDate: Util.FullDateStr;
  onDone: (output: DbTable.BorrowingBaseReportFromLoanTapeCalcs) => void;
};
export const BasicBorrowingBaseEvaluator = ({
  borrowingBaseMechanics,
  borrowingBaseReceivables,
  concentrationLimitCalculations,
  tapeBuilderNode,
  reportingDate,
  onDone,
}: BasicBorrowingBaseEvaluatorProps) => {
  const graphContext = useGraph();

  finleyAssert(
    TapeBuilderUI.operationNodes.operation_calcAgent.isNode(tapeBuilderNode),
    'node is not a calcAgent node',
  );
  const creditFacilityId = getCreditFacilityBySlug(
    graphContext,
    tapeBuilderNode.configuration.creditFacilitySlug,
  );
  finleyAssert(creditFacilityId, 'creditFacilityId not found');

  const [progressPercent, setProgressPercent] = useState(0);
  const [progressText, setProgressText] = useState('');

  const generateBasicBorrowingBaseReport = async () => {
    finleyAssert(
      borrowingBaseReceivables.length,
      'No receivables. Did you forget to run Calc-Agent?',
    );

    const output = await createBasicBorrowingBaseReportWithCalcs({
      borrowingBaseMechanics,
      borrowingBaseReceivables,
      concentrationLimitCalculations,
      progressCallback: async (
        operation: string,
        strProgress: string,
        percent: number,
      ) => {
        setProgressText(`${operation}: ${strProgress}`);
        setProgressPercent(percent);
        await new Promise((resolve) => setTimeout(resolve, 0));
      },
    });

    setProgressPercent(100);
    setProgressText('Done!');
    onDone(output);
    return output;
  };

  const filename = `BasicBorrowingBaseReport_${reportingDate}.json`;
  return (
    <ProgressWithDownloadButton
      onStart={generateBasicBorrowingBaseReport}
      buttonText="Basic Borrowing Base Report"
      progressPercent={progressPercent}
      progressText={progressText}
      filename={filename}
    />
  );
};
